import { Box, GlobalStyles } from '@mui/material';
import { useState } from 'react';
import { Outlet } from 'react-router-dom';
import { palette } from '@ahora-patagonia/design-tokens';
import { contrasteSobre } from '../modules/shared/branding/contraste.js';
import { EspacioPublicitario } from '../modules/shared/ads/EspacioPublicitario.js';
import { useVolverAlTope } from '../modules/shared/hooks/useVolverAlTope.js';
import { usePaginaPublica } from '../modules/shared/shell/paginaPublica.js';
import { NavDrawer } from './NavDrawer.js';
import { PublicFooter } from './PublicFooter.js';
import { PublicHeader } from './PublicHeader.js';

/**
 * Layout del sitio público. Sin auth y sin `window` en el camino de render: todo corre en
 * Node (SSR) y la hidratación no mueve un píxel.
 *
 * **El color del cliente entra por acá**: `Tenant.primaryColor` llega en el shell (dato del
 * loader, nunca `useEffect`) y se publica como CSS custom property. Los componentes que
 * *identifican* (volanta de categoría, numeración de portada, categoría activa) consumen
 * `var(--color-cliente)`; el theme de MUI queda estático y compartido entre requests.
 * Ese color nunca comunica estado ni urgencia — eso es del rojo editorial y de la tinta.
 */
export function PublicLayout() {
  const pagina = usePaginaPublica();
  const [navAbierta, setNavAbierta] = useState(false);

  useVolverAlTope();

  const colorCliente = pagina?.shell.tenant.primaryColor ?? palette.ink;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <GlobalStyles
        styles={{
          ':root': {
            '--color-cliente': colorCliente,
            '--sobre-cliente': contrasteSobre(colorCliente),
          },
        }}
      />

      <Box
        component="a"
        href="#contenido"
        sx={{
          position: 'absolute',
          left: -9999,
          zIndex: (t) => t.zIndex.tooltip,
          backgroundColor: palette.ink,
          color: '#fff',
          padding: '12px 20px',
          '&:focus-visible': { left: 0, top: 0 },
        }}
      >
        Saltar al contenido
      </Box>

      <PublicHeader shell={pagina?.shell ?? null} onAbrirNav={() => setNavAbierta(true)} />
      <NavDrawer
        shell={pagina?.shell ?? null}
        abierta={navAbierta}
        onCerrar={() => setNavAbierta(false)}
      />

      <Box
        component="main"
        id="contenido"
        sx={{
          flex: 1,
          width: '100%',
          maxWidth: 1440,
          mx: 'auto',
          px: { xs: '14px', sm: '20px', lg: '32px' },
          pt: 2,
        }}
      >
        <Outlet />
      </Box>

      {/* El zócalo: arriba del pie, en todas las páginas. Sin campaña, no existe. */}
      <Box sx={{ px: { xs: '14px', sm: '20px', lg: '32px' }, pt: 3 }}>
        <EspacioPublicitario ubicacion="FOOTER" ad={pagina?.ads.FOOTER} />
      </Box>

      <PublicFooter shell={pagina?.shell ?? null} />
    </Box>
  );
}
